import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Loading } from '@/components/loading';
import { ConsentCard } from './consent-card';
import { ConsentError } from './consent-error';
import {
  useApproveAuthorizeMutation,
  useDenyAuthorizeMutation,
  useValidateAuthorizeQuery,
} from '@/api/oauth';
import type { AuthorizeParams, ValidateOk } from '@/api/oauth';

const readParams = (search: URLSearchParams): AuthorizeParams | null => {
  const required = (k: string) => search.get(k) ?? '';
  const params: AuthorizeParams = {
    client_id: required('client_id'),
    redirect_uri: required('redirect_uri'),
    response_type: required('response_type'),
    code_challenge: required('code_challenge'),
    code_challenge_method: required('code_challenge_method'),
    scope: search.get('scope') ?? undefined,
    state: search.get('state') ?? undefined,
    resource: search.get('resource') ?? undefined,
  };
  if (
    !params.client_id ||
    !params.redirect_uri ||
    !params.response_type ||
    !params.code_challenge ||
    !params.code_challenge_method
  ) {
    return null;
  }
  return params;
};

const OauthAuthorize = () => {
  const [searchParams] = useSearchParams();
  const params = useMemo(() => readParams(searchParams), [searchParams]);

  const { data: validation, isLoading } = useValidateAuthorizeQuery(
    params as AuthorizeParams,
    { skip: !params },
  );

  // Pre-select what the client asked for; default to nothing (never full
  // access), so the user makes an explicit choice and Allow stays disabled
  // until they do. The server caps a confidential client at its service
  // account's allowed_scopes.
  const initialScopes = useMemo<string[]>(() => {
    if (!validation || !validation.ok) return [];
    return validation.requested_scopes;
  }, [validation]);

  const [scopes, setScopes] = useState<string[]>(initialScopes);

  const [approve, { isLoading: isApproving }] = useApproveAuthorizeMutation();
  const [deny, { isLoading: isDenying }] = useDenyAuthorizeMutation();

  const onAllow = useCallback(async () => {
    if (!params) return;
    const result = await approve({
      ...params,
      scopes,
    }).unwrap();
    window.location.assign(result.redirect_uri);
  }, [approve, params, scopes]);

  const onDeny = useCallback(async () => {
    if (!params) return;
    const result = await deny(params).unwrap();
    window.location.assign(result.redirect_uri);
  }, [deny, params]);

  if (!params) {
    return (
      <Layout variant='narrow'>
        <ConsentError
          title="We can't complete this sign-in"
          reason='This authorization request is missing required parameters.'
          detail='The application may be misconfigured, or the link you followed may have been truncated.'
        />
      </Layout>
    );
  }

  if (isLoading || !validation) {
    return (
      <Layout variant='narrow'>
        <Loading />
      </Layout>
    );
  }

  if (!validation.ok) {
    return (
      <Layout variant='narrow'>
        <ConsentError
          title="We can't complete this sign-in"
          reason={validation.error_description ?? validation.error}
          detail={
            validation.fatal
              ? 'The application is not recognised, or its redirect destination has not been registered with Kinesis.'
              : 'The authorization request was rejected.'
          }
        />
      </Layout>
    );
  }

  const { client } = validation as ValidateOk;

  return (
    <Layout variant='narrow'>
      <ConsentCard
        client={client}
        scopes={scopes}
        onScopesChange={setScopes}
        onAllow={onAllow}
        onDeny={onDeny}
        busy={isApproving || isDenying}
      />
    </Layout>
  );
};

export { OauthAuthorize };
